import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Paper,
    CircularProgress,
    Tabs,
    Tab,
    LinearProgress,
} from '@material-ui/core';
import { Bar, Doughnut } from 'react-chartjs-2';
import CohortReports from './CohortReports';



export default function Reports({ classes }) {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState('30');

    // General analytics state
    const [engagementMetrics, setEngagementMetrics] = useState({
        totalUsers: 0,
        completedSurveys: 0,
        todaySurveys: 0,
        monthSurveys: 0,
        completionRate: 0,
    });
    const [entrepreneurialInsights, setEntrepreneurialInsights] = useState({
        archetypeDistribution: [],
        dimensionAverages: {},
    });

    useEffect(() => {
        fetchGeneralAnalytics();
    }, []);

    useEffect(() => {
        if (activeTab === 0) {
            fetchGeneralAnalytics();
        }
    }, [dateRange, activeTab]);

    const fetchGeneralAnalytics = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/reports/general?dateRange=${dateRange}`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setEngagementMetrics(data.engagementMetrics);
                setEntrepreneurialInsights(data.entrepreneurialInsights);
            }
        } catch (error) {
            console.error('Error fetching general analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Chart configurations for General Analytics
    const archetypeChartData = {
        labels: entrepreneurialInsights.archetypeDistribution.map(item => item.archetype),
        datasets: [
            {
                data: entrepreneurialInsights.archetypeDistribution.map(item => item.count),
                backgroundColor: [
                    'rgba(217, 98, 48, 0.8)',
                    'rgba(57, 172, 126, 0.8)',
                    'rgba(99, 132, 255, 0.8)',
                    'rgba(152, 102, 255, 0.8)',
                    'rgba(255, 159, 64, 0.8)',
                ],
                borderColor: [
                    'rgb(217, 98, 48)',
                    'rgb(57, 172, 126)',
                    'rgb(99, 132, 255)',
                    'rgb(152, 102, 255)',
                    'rgb(255, 159, 64)',
                ],
                borderWidth: 1,
            },
        ],
    };

    const dimensionComparisonData = {
        labels: ['Innovation', 'Risk Taking', 'Planning', 'Execution', 'Intrinsic Motivation'],
        datasets: [
            {
                label: 'Average Scores',
                data: [
                    entrepreneurialInsights.dimensionAverages.innovation || 0,
                    entrepreneurialInsights.dimensionAverages.risk_taking || 0,
                    entrepreneurialInsights.dimensionAverages.planning || 0,
                    entrepreneurialInsights.dimensionAverages.execution || 0,
                    entrepreneurialInsights.dimensionAverages.intrinsic_motivation || 0,
                ],
                backgroundColor: 'rgba(57, 172, 126, 0.7)',
                borderColor: 'rgb(57, 172, 126)',
                borderWidth: 1,
            },
        ],
    };

    const getCompletionColor = (rate) => {
        if (rate >= 70) return '#4caf50';
        if (rate >= 50) return '#ff9800';
        return '#f44336';
    };

    return (
        <div className={classes.mainContent}>
            <Typography variant="h4" align="center" gutterBottom>
                Reports
            </Typography>

            <Paper className={classes.tableContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab label="Business Overview" />
                    <Tab label="Cohort Analysis" />
                </Tabs>

                <Box p={3}>
                    {/* Business Overview Tab */}
                    {activeTab === 0 && (
                        <>
                            {loading ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Box mb={3}>
                                        <TextField
                                            select
                                            label="Date Range"
                                            value={dateRange}
                                            onChange={(e) => setDateRange(e.target.value)}
                                            variant="outlined"
                                            style={{ minWidth: 200 }}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <MenuItem value="7">Last 7 days</MenuItem>
                                            <MenuItem value="30">Last 30 days</MenuItem>
                                            <MenuItem value="90">Last 90 days</MenuItem>
                                            <MenuItem value="365">Last year</MenuItem>
                                        </TextField>
                                    </Box>

                                    {/* Engagement Metrics */}
                                    <Grid container spacing={3} style={{ marginBottom: 24 }}>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Card variant="outlined" style={{ padding: 16, textAlign: 'center' }}>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Total Users
                                                </Typography>
                                                <Typography variant="h4">
                                                    {engagementMetrics.totalUsers}
                                                </Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Card variant="outlined" style={{ padding: 16, textAlign: 'center' }}>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Completed Surveys
                                                </Typography>
                                                <Typography variant="h4">
                                                    {engagementMetrics.completedSurveys}
                                                </Typography>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
    <Card
        variant="outlined"
        style={{
            padding: 16,
            textAlign: 'center',
            minHeight: 260
        }}
    >
        <Typography color="textSecondary" gutterBottom>
            Completion Rate
        </Typography>

        <Typography
            variant="h4"
            style={{
                color: getCompletionColor(
                    engagementMetrics.completionRate
                )
            }}
        >
            {engagementMetrics.completionRate}%
        </Typography>

        <LinearProgress
            variant="determinate"
            value={engagementMetrics.completionRate}
            style={{ marginTop: 8, marginBottom: 12 }}
        />

        {/* Status */}
        <Typography
            variant="subtitle2"
            style={{
                fontWeight: 'bold',
                color: getCompletionColor(
                    engagementMetrics.completionRate
                ),
                marginBottom: 8
            }}
        >
            {engagementMetrics.completionInsight?.status?.toUpperCase()}
        </Typography>

        {/* Interpretation */}
        <Typography
            variant="body2"
            style={{
                marginBottom: 10
            }}
        >
            {
                engagementMetrics.completionInsight
                    ?.interpretation
            }
        </Typography>

        {/* Recommended Action */}
        <Typography
            variant="caption"
            display="block"
            style={{
                fontStyle: 'italic'
            }}
        >
            Recommended Action:
        </Typography>

        <Typography variant="body2">
            {
                engagementMetrics.completionInsight
                    ?.action
            }
        </Typography>
    </Card>
</Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Card variant="outlined" style={{ padding: 16, textAlign: 'center' }}>
                                                <Typography color="textSecondary" gutterBottom>
                                                    This Month
                                                </Typography>
                                                <Typography variant="h4">
                                                    {engagementMetrics.monthSurveys}
                                                </Typography>
                                                <Typography variant="body2">New Surveys</Typography>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3}>
                                        {/* Entrepreneurial Archetypes */}
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined" style={{ padding: 16 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Entrepreneurial Archetypes
                                                </Typography>
                                                <div style={{ height: 300 }}>
                                                    <Doughnut
                                                        data={archetypeChartData}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: {
                                                                    position: 'bottom'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </Card>
                                        </Grid>

                                        {/* Key Dimension Performance */}
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined" style={{ padding: 16 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Key Dimension Performance
                                                </Typography>
                                                <div style={{ height: 300 }}>
                                                    <Bar
                                                        data={dimensionComparisonData}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            indexAxis: 'y',
                                                            scales: {
                                                                x: {
                                                                    beginAtZero: true,
                                                                    max: 20
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    {/* Cohort Analysis Tab - Reuse CohortReports component */}
                    {activeTab === 1 && <CohortReports classes={classes} />}
                </Box>
            </Paper>
        </div>
    );
}